import { useEffect, useState } from 'react';
import { DEFAULT_SITE_CONTENT } from './defaultSiteContent';
import { parseSiteContentPayload } from './parseSiteContent';
import type { I3SiteContentV1 } from './types';
import { getPublicPortalSiteContentUrl, getStaticI3CmsUrl } from './i3CmsEndpoints';

export type UseI3SiteContentResult = {
  content: I3SiteContentV1;
  loading: boolean;
  error: Error | null;
  /** true after fetch attempt finished (success or failure) */
  ready: boolean;
  /** When set, public pages loaded CMS from the API (SQL) */
  source: 'sql-api' | 'static' | null;
};

/**
 * Loads I-3 CMS JSON.
 * 1. If `REACT_APP_PORTAL_PUBLIC_CMS_BASE` is set, tries GET
 *    `{base}/api/public/portal/sites/{REACT_APP_I3_PORTAL_SLUG}/site-content` (SQL publish).
 * 2. Otherwise uses `REACT_APP_I3_CMS_URL` or `/cms/i3/site-content.json`.
 */
export function useI3SiteContent(): UseI3SiteContentResult {
  const [content, setContent] = useState<I3SiteContentV1>(() => DEFAULT_SITE_CONTENT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [ready, setReady] = useState(false);
  const [source, setSource] = useState<'sql-api' | 'static' | null>(null);

  useEffect(() => {
    const staticUrl = getStaticI3CmsUrl();
    const publicApiUrl = getPublicPortalSiteContentUrl();
    let cancelled = false;

    (async () => {
      try {
        if (publicApiUrl) {
          try {
            const res = await fetch(publicApiUrl, { cache: 'no-store' });
            if (res.ok) {
              const body = (await res.json()) as { content?: unknown };
              if (body?.content !== undefined && body.content !== null) {
                if (cancelled) return;
                setContent(parseSiteContentPayload(body.content));
                setError(null);
                setSource('sql-api');
                return;
              }
            }
          } catch {
            /* fall through to static */
          }
        }

        const res = await fetch(staticUrl, { cache: 'no-store' });
        if (!res.ok) throw new Error(`CMS fetch ${res.status}`);
        const json: unknown = await res.json();
        if (cancelled) return;
        setContent(parseSiteContentPayload(json));
        setError(null);
        setSource('static');
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setContent(DEFAULT_SITE_CONTENT);
        setSource(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { content, loading, error, ready, source };
}
